import React, { useEffect, useRef, useState } from "react";
import { AiOutlineClockCircle } from "react-icons/ai";
import { BsStopwatch, BsGear } from "react-icons/bs";
import { BiUnlink, BiPowerOff } from "react-icons/bi";
import { TbRestore } from "react-icons/tb";
import { MdSystemUpdateAlt, MdCheck } from "react-icons/md";

const Row = ({
  icon,
  label,
  danger,
  activeRed,
  onClick,
  withDivider,
  disabled,
  title,
}) => (
  <>
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      title={title}
      className={`tw-w-full tw-flex tw-items-center tw-gap-3 tw-text-left tw-px-5 tw-py-3 hover:tw-bg-gray-50 ${
        danger || activeRed ? "tw-text-red-600" : "tw-text-gray-800"
      } ${disabled ? "tw-opacity-50 tw-cursor-not-allowed" : ""}`}
      disabled={disabled}
    >
      <span
        className={`tw-flex tw-items-center ${
          danger ? "tw-text-red-500" : "tw-text-gray-700"
        }`}
      >
        {icon}
      </span>
      <span className="tw-text-[15px]">{label}</span>
      {activeRed && (
        <span className="tw-ml-auto tw-text-gray-400">
          <MdCheck size={18} />
        </span>
      )}
    </button>
    {withDivider && <div className="tw-border-t tw-border-gray-200" />}
  </>
);

const DeviceCardMenu = ({
  anchorRect,
  bulkCount = 1,
  isUnderMaintenance = false,
  onAddMaintenance,
  onUnderMaintenance,
  onConfigure,
  onUnbind,
  onReboot,
  onUpdateFirmware,
  onFactoryReset,
  onClose,
}) => {
  const ref = useRef(null);
  const [style, setStyle] = useState(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [onClose]);

  useEffect(() => {
    if (!anchorRect) return;
    const menuWidth = 320; // tw-w-80 = 20rem ≈ 320px

    let left = Math.min(
      anchorRect.right - menuWidth,
      window.innerWidth - menuWidth - 8
    );
    left = Math.max(8, left);
    let top = anchorRect.bottom + 8;

    setStyle({ position: "fixed", left, top, zIndex: 9999, width: menuWidth });

    const id = requestAnimationFrame(() => {
      if (!ref.current) return;
      const h = ref.current.offsetHeight;
      const belowSpace = window.innerHeight - anchorRect.bottom - 8;
      const aboveSpace = anchorRect.top - 8;
      if (belowSpace < h && aboveSpace > h) {
        setStyle({
          position: "fixed",
          left,
          top: anchorRect.top - h - 8,
          zIndex: 9999,
          width: menuWidth,
        });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [anchorRect]);

  const containerProps = style
    ? {
        style,
        className:
          "tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-border tw-border-gray-100 tw-w-80",
      }
    : {
        className:
          "tw-absolute tw-right-0 tw-top-9 tw-z-[9999] tw-w-80 tw-rounded-2xl tw-bg-white tw-shadow-2xl tw-border tw-border-gray-100",
      };

  return (
    <div ref={ref} {...containerProps}>
      <Row
        icon={<AiOutlineClockCircle size={18} />}
        label="Add Scheduled Maintenance"
        onClick={onAddMaintenance}
        withDivider
      />
      <Row
        icon={<BsStopwatch size={18} />}
        label={isUnderMaintenance ? "Clear Maintenance" : "Under Maintenance"}
        activeRed={isUnderMaintenance}
        onClick={onUnderMaintenance}
        withDivider
      />
      <Row
        icon={<BsGear size={18} />}
        label="Configure"
        onClick={onConfigure}
        withDivider
      />
      <Row
        icon={<BiUnlink size={18} />}
        label="Unbind Device"
        onClick={onUnbind}
        withDivider
      />
      <Row
        icon={<MdSystemUpdateAlt size={18} />}
        label="Update firmware"
        onClick={onUpdateFirmware}
        withDivider
        disabled={bulkCount > 1}
        title={
          bulkCount > 1
            ? "Select exactly 1 device to update firmware"
            : undefined
        }
      />
      <Row
        icon={<TbRestore size={18} />}
        label="Factory reset"
        onClick={onFactoryReset}
        withDivider
      />
      <Row
        icon={<BiPowerOff size={18} />}
        label="Reboot device"
        danger
        onClick={onReboot}
      />
    </div>
  );
};

export default DeviceCardMenu;
